/**
 * The Planner's hand-off, from the fragment it arrived in to the form it fills (KTD4, KTD9).
 *
 * Modelled on Collab's `invite-token.ts`. This runs **first** in `main.tsx`, before
 * `ClerkProvider` mounts and before PostHog starts:
 *
 * - PostHog reads `location.href` when it initialises. The ask text in the address bar at
 *   that moment would be a teacher's words in a vendor's store.
 * - Clerk's `SignIn routing="hash"` writes its own steps into `location.hash`, and the
 *   hosted sign-in comes back through a redirect, which does not keep a fragment.
 *
 * So the fragment is read, validated, held in `sessionStorage` (per tab, gone with it) and in
 * memory for this page load, and the address bar is sent to `/`. `App` reads the held hand-off once it has booted, fills
 * the form, and clears it. Nothing here submits: no ask is spent until the teacher presses
 * the button (R9).
 */
import { parsePlannerHandoff, PLANNER_HANDOFF_PATH, type PlannerHandoff } from '../core/planner-handoff';

/** One key, read and written only in this file. */
export const PLANNER_HANDOFF_KEY = 'osa.planner-handoff';

/** The browser edge, passed in so a test can hand over a fake. */
export interface HandoffContext {
  readonly pathname: string;
  readonly hash: string;
  readonly replaceState: (url: string) => void;
  /** Null whenever storage is unavailable — a private window, or blocked site data. */
  readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

const safeStorage = (): HandoffContext['storage'] => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/** The real browser, read once per call. */
export function browserHandoffContext(): HandoffContext {
  const { hash, pathname } = window.location;
  return {
    pathname,
    hash,
    replaceState: (url) => { window.history.replaceState(null, '', url); },
    storage: safeStorage(),
  };
}

/**
 * The held fragment for this page load, beside `sessionStorage`. When storage is unavailable or
 * refuses, this is what lets `App` still apply the hand-off a teacher arrived with in this page
 * load; it is gone on reload, as a fragment would be.
 */
let inMemory: string | null = null;

/**
 * On `/from-planner`: hold a valid hand-off (dropping any older one), and send the address
 * bar to `/` whatever the fragment held. On any other path: touch nothing — a fragment there
 * is Clerk's or an anchor. Answers with the hand-off now held, so the return leg of a sign-in
 * round trip finds it too.
 */
export function capturePlannerHandoff(context: HandoffContext = browserHandoffContext()): PlannerHandoff | null {
  if (context.pathname.replace(/\/+$/, '') !== PLANNER_HANDOFF_PATH) return heldPlannerHandoff(context);
  const handoff = parsePlannerHandoff(context.hash);
  context.replaceState('/');
  if (!handoff) {
    clearPlannerHandoff(context);
    return null;
  }
  inMemory = context.hash.replace(/^#/, '');
  try {
    context.storage?.setItem(PLANNER_HANDOFF_KEY, inMemory);
  } catch {
    // Storage refused. The in-memory copy still fills the form in this page load.
  }
  return handoff;
}

/** The hand-off being held, re-validated, or null. */
export function heldPlannerHandoff(context: HandoffContext = browserHandoffContext()): PlannerHandoff | null {
  let stored: string | null = null;
  try {
    stored = context.storage?.getItem(PLANNER_HANDOFF_KEY) ?? null;
  } catch {
    // Storage refused; the in-memory copy answers.
  }
  const held = stored ?? inMemory;
  return held === null ? null : parsePlannerHandoff(held);
}

/** Spend it, once the form has been filled. */
export function clearPlannerHandoff(context: HandoffContext = browserHandoffContext()): void {
  inMemory = null;
  try {
    context.storage?.removeItem(PLANNER_HANDOFF_KEY);
  } catch {
    // Nothing to do.
  }
}
